import { useTranslation } from 'react-i18next';
import { S, Icon, DataCard, formatCurrency } from '../../regureStyles';
import { useFeature } from '../../../hooks/useAccess';

const OverviewTab = ({ carrier }) => {
  const { t } = useTranslation();
  // Every bordereaux-specific field on this card is gated on the tenant flag.
  //
  // The tab strip was gated first, which removed the Bordereaux tab and the
  // Generate action — but this Overview card kept rendering "BORDEREAUX STATUS
  // Pending" and a full BORDEREAUX SCHEDULE (frequency, day, format,
  // auto-send) on a tenant with the module off. Removing the door while
  // leaving the sign up is not gating: a disabled module must not appear as a
  // status, a schedule or an empty placeholder.
  const bordereauxEnabled = useFeature('bordereaux');
  const detailItems = [
    { icon: 'user',     label: t('screens.riskCarriers.overviewPrimaryContact', { defaultValue: 'Primary Contact' }), value: carrier.contact || '—' },
    { icon: 'mail',     label: t('screens.riskCarriers.overviewEmail',          { defaultValue: 'Email' }),           value: carrier.email || '—' },
    { icon: 'phone',    label: t('screens.riskCarriers.overviewPhone',          { defaultValue: 'Phone' }),           value: carrier.phone || '—' },
    { icon: 'map-pin',  label: t('screens.riskCarriers.overviewLocation',       { defaultValue: 'Location' }),        value: [carrier.city, carrier.canton].filter(Boolean).join(', ') || '—' },
    { icon: 'building', label: t('screens.riskCarriers.overviewAddress',        { defaultValue: 'Address' }),         value: carrier.address || '—' },
  ];
  const bordereauStatusLabel = carrier.bordereau === 'Generated'
    ? t('screens.riskCarriers.bordereauGenerated', { defaultValue: 'Generated' })
    : t('screens.riskCarriers.bordereauPending',   { defaultValue: 'Pending' });
  const portfolioMetrics = [
    { label: t('screens.riskCarriers.overviewActivePolicies',   { defaultValue: 'Active Policies' }),   value: carrier.activePolicies,                       color: S.textPrimary },
    { label: t('screens.riskCarriers.overviewMonthlyPremium',   { defaultValue: 'Monthly Premium' }),   value: formatCurrency(carrier.monthlyPremium),       color: S.burgundy },
    { label: t('screens.riskCarriers.overviewAnnualVolume',     { defaultValue: 'Annual Volume' }),     value: formatCurrency(carrier.monthlyPremium * 12),  color: S.textPrimary },
    ...(bordereauxEnabled
      ? [{ label: t('screens.riskCarriers.overviewBordereauxStatus', { defaultValue: 'Bordereaux Status' }), value: bordereauStatusLabel, color: carrier.bordereau === 'Generated' ? '#10b981' : '#f59e0b' }]
      : []),
  ];
  const scheduleItems = [
    { label: t('screens.riskCarriers.overviewFrequency',  { defaultValue: 'Frequency' }),    value: carrier.bordereauxConfig?.frequency || 'monthly' },
    { label: t('screens.riskCarriers.overviewDayOfMonth', { defaultValue: 'Day of Month' }), value: `${carrier.bordereauxConfig?.dayOfMonth || 5}${t('screens.riskCarriers.overviewDaySuffix', { defaultValue: 'th' })}` },
    { label: t('screens.riskCarriers.overviewFormat',     { defaultValue: 'Format' }),       value: (carrier.bordereauxConfig?.format || 'xlsx').toUpperCase() },
    { label: t('screens.riskCarriers.overviewAutoSend',   { defaultValue: 'Auto-Send' }),    value: carrier.bordereauxConfig?.autoSend
        ? t('screens.riskCarriers.overviewYes', { defaultValue: 'Yes' })
        : t('screens.riskCarriers.overviewNo',  { defaultValue: 'No' }) },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      <DataCard>
        <div style={{ fontSize: 14, fontWeight: 700, color: S.textPrimary, marginBottom: 14 }}>{t('screens.riskCarriers.overviewDetailsTitle', { defaultValue: 'Carrier Details' })}</div>
        {detailItems.map(item => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: S.bgCard,
                border: `1px solid ${S.borderLight}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon name={item.icon} color={S.textMuted} size={16} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>{item.label}</div>
              <div style={{ fontSize: 13, color: S.textPrimary, fontWeight: 500, marginTop: 2 }}>{item.value}</div>
            </div>
          </div>
        ))}
        <div style={{ marginTop: 8, paddingTop: 12, borderTop: `1px solid ${S.borderLight}` }}>
          <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>{t('screens.riskCarriers.overviewProductsCovered', { defaultValue: 'Products Covered' })}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(carrier.products || []).map(p => (
              <span key={p} style={{ padding: '4px 10px', background: `rgba(128,0,32,0.07)`, color: S.burgundy, borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                {p}
              </span>
            ))}
            {carrier.products?.length === 0 && <span style={{ fontSize: 12, color: S.textMuted }}>{t('screens.riskCarriers.overviewNoProducts', { defaultValue: 'None configured' })}</span>}
          </div>
        </div>
      </DataCard>

      <DataCard>
        <div style={{ fontSize: 14, fontWeight: 700, color: S.textPrimary, marginBottom: 14 }}>{t('screens.riskCarriers.overviewPortfolio', { defaultValue: 'Portfolio Summary' })}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {portfolioMetrics.map(m => (
            <div key={m.label} style={{ padding: '12px 14px', background: S.bgCard, borderRadius: 10, border: `1px solid ${S.borderLight}` }}>
              <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{m.label}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: m.color }}>{m.value}</div>
            </div>
          ))}
        </div>
        {bordereauxEnabled && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${S.borderLight}` }}>
            <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>{t('screens.riskCarriers.overviewSchedule', { defaultValue: 'Bordereaux Schedule' })}</div>
            <div style={{ display: 'flex', gap: 16 }}>
              {scheduleItems.map(m => (
                <div key={m.label}>
                  <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 600 }}>{m.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary, textTransform: 'capitalize' }}>{m.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DataCard>
    </div>
  );
};

export default OverviewTab;
